import { Anchor, AuszugAtButton, Show } from 'components/atoms';
import { ContentRow } from 'components/molecules';
import { NamensTimeline, PlusRow, Scheinunternehmen } from 'components/profiles';
import Adresse from 'components/profiles/Adresse/Adresse';
import React from 'react';
import { UnternehmensStatus } from 'types';
import { UnternehmenDto } from 'types/unternehmen';
import { useTranslation } from 'utils/hooks';
import { TranslationKey } from 'utils/hooks/useTranslation';

interface StammdatenProps
{
  unternehmen: UnternehmenDto;
}

const Stammdaten: React.FC<StammdatenProps> = (props) =>
{
  const { unternehmen } = props;
  const { t } = useTranslation(['common', 'profiles', 'rechtsformen', 'formats']);
  const statusKey = `common:profiles.status.${unternehmen.status === UnternehmensStatus.Gelöscht ? 'GelöschtMit' : unternehmen.status}` as TranslationKey;
  const status = t(statusKey, { date: unternehmen.statusSeit }) as string;


  return (
    <>

      {/* Status */}
      <ContentRow id="allgemeines" title={t('profiles:unternehmen.status.label')} groupItem>
        { unternehmen.scheinunternehmenSeit ? <Scheinunternehmen status={status} /> : status }
      </ContentRow>

      {/* FbNr */}
      <ContentRow title={t('profiles:unternehmen.fbNr.label')} groupItem>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '2rem' }}>
        { unternehmen.fbNr }
          <a href={`https://firmenbuch.auszug.at/unternehmen/${unternehmen.fbNr}`}
             target="_blank"
             rel="noreferrer"
             style={{ textDecoration: 'none' }}
          >
          <AuszugAtButton text={t('profiles:unternehmen.fbNr.cta')} />
          </a>
        </div>
      </ContentRow>

      {/* Wortlaute Timeline */}
      <ContentRow title={t('profiles:unternehmen.wortlaute.label')} groupItem>
        {unternehmen.wortlaute.length > 1 ?
          (<>
          <NamensTimeline
            items={[...[...unternehmen.wortlaute].reverse().map((wortlaut) => ({
              zeitpunkt: wortlaut.zeitpunkt,
              text: wortlaut.item.fullName,
            })), ...Array(unternehmen.premiumInfo?.namen ?? 0).fill({ zeitpunkt: '12-12-2024', text: 'Wortlaut AG' })]}
            change={t('profiles:unternehmen.wortlaute.change')}
            isFree={unternehmen.tier === 'Free'}
          />
          {unternehmen.tier === 'Free' && unternehmen.premiumInfo && unternehmen.premiumInfo.namen > 0 && (
            <div style={{ marginTop:'2rem' }}>
              <PlusRow text={t('profiles:plusTeaser.plusRow.historischerWortlaut', { count: unternehmen.premiumInfo.namen })} />
            </div>
          )}
          </>
          )
          : unternehmen.name}
      </ContentRow>

      {/* Eintragungsdatum */}
      <ContentRow title={t('profiles:unternehmen.eintragungsdatum.label')} groupItem>
        { t('formats:date', { value: unternehmen.eintragungsdatum }) }
      </ContentRow>

      {/* Ausländisches Register */}
      {(unternehmen.auslandsInfo?.register ?? '' !== '') && (
        <ContentRow title={t('profiles:unternehmen.auslandsInfo.register')} groupItem>
        {unternehmen.auslandsInfo?.register}
        </ContentRow>
      )}
      {(unternehmen.auslandsInfo?.registerNr ?? '' !== '') && (
        <ContentRow title={t('profiles:unternehmen.auslandsInfo.registerNr')} groupItem>
          {unternehmen.auslandsInfo?.registerNr}
        </ContentRow>
      )}


      {/* Rechtsform */}
      <ContentRow title={t('profiles:unternehmen.rechtsform.label')} groupItem>
        { unternehmen.auslandsInfo
          ? unternehmen.rechtsform.bezeichnung + (unternehmen.auslandsInfo.personalstatut === null ? '' : ` (${unternehmen.auslandsInfo.personalstatut})`)
          : t(`rechtsformen:list.${unternehmen.rechtsform.code}`) }
      </ContentRow>

      {/* Scheinunternehmen seit */}
      <Show when={unternehmen.scheinunternehmenSeit !== null}>
        <ContentRow title={t('profiles:unternehmen.scheinunternehmen.label')} groupItem>
          { t('formats:date', { value: unternehmen.scheinunternehmenSeit }) }
        </ContentRow>
      </Show>

      {/* Aktien */}
      <Show when={unternehmen.aktie !== null && (unternehmen.aktie.aktienArt === 'Nennbetragsaktie' || unternehmen.aktie.anzahl > 0)}>
        <ContentRow title={t('profiles:unternehmen.aktien.label')} groupItem>
          { t(`profiles:unternehmen.aktien.${unternehmen.aktie?.aktienArt}` as TranslationKey, { count: unternehmen.aktie?.anzahl }) }
        </ContentRow>
      </Show>

      {/* Kapital */}
      <Show when={unternehmen.kapital !== null}>
        <ContentRow
          title={t(`profiles:unternehmen.kapital.${unternehmen.rechtsform.code === 'GEN' ? 'labelGAH' : 'label'}`)}
          groupItem
        >
          { t('formats:money', { value: unternehmen.kapital }) }
        </ContentRow>
      </Show>
      {unternehmen.unternehmenswertanteil !== null && (
        <ContentRow
          title={t('profiles:unternehmen.unternehmenswertanteil.label')}
          groupItem
        >
          { t('formats:money', { value: unternehmen.unternehmenswertanteil }) }
        </ContentRow>
      )}

      {/* Stichtag Jahresabschluss */}
      <Show when={unternehmen.stichtagJab !== null}>
        <ContentRow title={t('profiles:unternehmen.stichtagJab.label')} groupItem>
          { t('formats:stichtag', { value: unternehmen.stichtagJab }) }
        </ContentRow>
      </Show>

      {/* Stichtag Kapitalabschluss */}
      <Show when={unternehmen.stichtagKab !== null}>
        <ContentRow title={t('profiles:unternehmen.stichtagKab.label')} groupItem>
          { t('formats:stichtag', { value: unternehmen.stichtagKab }) }
        </ContentRow>
      </Show>

      {/* GLNs */}
      {unternehmen.glns.length > 0 && (
        <ContentRow
          title={t('profiles:unternehmen.glns.label', { count: unternehmen.glns?.length ?? 1 })}
          groupItem
        >
          {unternehmen.glns.length == 1 ? (
            unternehmen.glns[0]
          ) :
            (<ul style={{ margin: 0, paddingLeft: '1rem' }}>
              {unternehmen.glns.sort().map(gln => (<li key={gln}>{gln}</li>))}
            </ul>)}
        </ContentRow>
      )}

      {/* Geschäftszweige */}
      <Show when={unternehmen.rechtsform.code !== 'PST' && unternehmen.stiftungszweck === null}>
        <ContentRow
          title={t('profiles:unternehmen.geschäftszweige.label', { count: unternehmen.geschäftszweige?.length ?? 1 })}
          groupItem
        >
          {unternehmen.geschäftszweige ?? t('profiles:notSpecified')}
        </ContentRow>
      </Show>

      {/* Branchen */}
      {unternehmen.tier !== 'Free' && unternehmen.branchen && unternehmen.branchen.length > 0 && (
        <ContentRow
          title={t('profiles:unternehmen.branchen.label', { count: unternehmen.branchen.length })}
          tooltip={t('profiles:unternehmen.branchen.tooltip')}
          groupItem
        >
          {unternehmen.branchen.length == 1 ? (
            t(`branchen:list.${unternehmen.branchen[0]}`)
          ) :
            (<ul style={{ margin: 0, paddingLeft: '1rem' }}>
           {unternehmen.branchen.map(branchenCode => [branchenCode, t(`branchen:list.${branchenCode}`)]).sort((a, b) =>a[1].localeCompare(b[1])).map(([branchenCode, text]) => (<li key={branchenCode}>{text}</li>))}
          </ul>)
          }
        </ContentRow>
      )}

      {/* Stiftungszweck */}
      <Show when={unternehmen.rechtsform.code === 'PST' || unternehmen.stiftungszweck !== null}>
        <ContentRow
          title={t('profiles:unternehmen.stiftungszweck.label')}
          groupItem
        >
          {unternehmen.stiftungszweck ?? t('profiles:notSpecified')}
        </ContentRow>
      </Show>

      {/* Vertretungsbefugnis */}
      <Show when={unternehmen.vertretungsbefugnis !== null}>
        <ContentRow
          title={t('profiles:unternehmen.vertretungsbefugnis.label')}
          groupItem
        >
          {unternehmen.vertretungsbefugnis}
        </ContentRow>
      </Show>

      {/* Webseiten */}
      <Show when={unternehmen.webseiten.length > 0}>
        <ContentRow
          title={t('profiles:unternehmen.webseiten.label', { count: unternehmen.webseiten.length })}
          groupItem
        >
          {unternehmen.webseiten.map((url) => (
            <div key={url}>
              <Anchor href={url} target="_blank" external>
                { url }
              </Anchor>
            </div>
          ))}
        </ContentRow>
      </Show>

      {/* Sitz */}
      <Show when={unternehmen.sitz !== null}>
        <ContentRow
          title={t('profiles:unternehmen.sitz.label')}
          groupItem
        >
          {unternehmen.sitz}
        </ContentRow>
      </Show>

      {/* Adresse */}
      <Adresse unternehmen={unternehmen} />
    </>
  );
};

export default Stammdaten;
